<script setup>
import { ref, computed } from 'vue';
import { useCurrency } from '@composables/useCurrency';
import { trans } from '@elements/translation';
import Button from '@atoms/components/Button.vue';
import { useDataTableStore } from '@stores/useDataTableStore';
import useInvoiceStore from '../store/invoices';
import useSubscriptionStore from '../store/subscriptions';

const { currencyField } = useCurrency();

const emit = defineEmits([ 'close' ]);
const invoiceStore = useInvoiceStore();
const subscriptionStore = useSubscriptionStore();
const datatableStore = useDataTableStore();

const { isLoading, createInvoice } = invoiceStore;

const localTitle = ref('');
const price = ref(null);
const localPrice = currencyField(price);

const titleInteracted = ref(false);
const priceInteracted = ref(false);

const titleError = computed(() => (!localTitle.value
  ? trans('gestion.invoices.slider.invoice.error.empty_title')
  : ''));

const priceError = computed(() => {
  if (!localPrice.value) {
    return trans('gestion.invoices.slider.invoice.error.empty_price');
  }

  if (localPrice.value <= 0) {
    return trans('gestion.invoices.slider.invoice.error.invalid_price');
  }

  return null;
});

const isFormValid = computed(() => !titleError.value && !priceError.value && !isLoading());

const doCreateInvoice = async () => {
  titleInteracted.value = true;
  priceInteracted.value = true;

  if (!isFormValid.value) {
    return;
  }

  const { invoice } = await createInvoice(
    subscriptionStore.selectedSubscriptionData.id,
    localTitle.value,
    price.value,
  );

  if (invoice) {
    datatableStore.addData(invoice);
  }

  emit('close');
};

const onTitleInput = () => {
  titleInteracted.value = true;
};

const onPriceInput = () => {
  priceInteracted.value = true;
};
</script>

<template>
  <div class="slider-create-invoice">
    <div class="slider-create-invoice__content">
      <div>
        <label for="invoice-title">{{ trans('gestion.invoices.slider.field.title') }} {{ trans('global.required') }}</label>
        <input
          id="invoice-title"
          v-model="localTitle"
          :class="{ 'error': titleInteracted && titleError }"
          type="text"
          :placeholder="trans('gestion.invoices.slider.field.placeholder')"
          @input="onTitleInput"
        >
        <span v-if="titleInteracted && titleError" class="error-message">{{ titleError }}</span>
      </div>
      <div>
        <label for="invoice-price">{{ trans('gestion.invoices.slider.field.price') }} {{ trans('global.required') }}</label>
        <div class="slider-create-invoice__input">
          <div class="input-text input-text--legend input-icon">
            <input
              id="invoice-price"
              v-model="localPrice"
              type="number"
              inputmode="numeric"
              name="price"
              :class="{ 'error': priceInteracted && priceError }"
              @input="onPriceInput"
            >
            <span>
              {{ trans('global.no_tax') }}
            </span>
          </div>
          <span v-if="priceInteracted && priceError" class="error-message">{{ priceError }}</span>
        </div>
      </div>
    </div>
    <div class="slider-create-invoice__actions">
      <Button variant="secondary" @click="emit('close')">
        {{ trans('global.cancel') }}
      </Button>
      <Button
        variant="primary"
        :class="{ 'loading-btn': isLoading() }"
        :disabled="!isFormValid"
        @click="doCreateInvoice"
      >
        {{ trans('global.validate') }}
      </button>
    </div>
  </div>
</template>
